import React from 'react';
import { Card } from '../../components/ui/card';
import { Activity, Languages, FileText, Cpu, BarChart2, LogIn } from 'lucide-react';

const FEATURE_CONFIG = {
  'Translation':  { color: 'bg-indigo-100 text-indigo-700',  icon: Languages,  dot: 'bg-indigo-500' },
  'MTPE Pro':     { color: 'bg-purple-100 text-purple-700',  icon: FileText,   dot: 'bg-purple-500' },
  'CAT Tool':     { color: 'bg-blue-100 text-blue-700',      icon: Cpu,        dot: 'bg-blue-500'   },
  'TQA':          { color: 'bg-amber-100 text-amber-700',    icon: BarChart2,  dot: 'bg-amber-500'  },
  'Logged in':    { color: 'bg-emerald-100 text-emerald-700',icon: LogIn,      dot: 'bg-emerald-500'},
};

function timeSince(isoString) {
  if (!isoString) return '';
  const secs = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

function UserRow({ user }) {
  const cfg = FEATURE_CONFIG[user.feature] || {
    color: 'bg-zinc-100 text-zinc-700',
    icon: Activity,
    dot: 'bg-zinc-400',
  };
  const Icon = cfg.icon;
  const details = user.details || {};

  const detailStr = [
    details.words && `${details.words} words`,
    details.langs,
    details.style,
    details.language,
  ].filter(Boolean).join(' · ');

  return (
    <div className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-0">
      {/* Live pulse dot */}
      <div className="relative flex-shrink-0">
        <span className={`block w-2.5 h-2.5 rounded-full ${cfg.dot} animate-pulse`} />
      </div>

      {/* Email */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-zinc-800 truncate">{user.masked_email}</p>
        {detailStr && (
          <p className="text-xs text-zinc-400 truncate">{detailStr}</p>
        )}
      </div>

      {/* Feature badge */}
      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${cfg.color}`}>
        <Icon className="h-3 w-3" />
        {user.feature}
      </span>

      {/* Time */}
      <span className="text-xs text-zinc-400 flex-shrink-0 w-14 text-right">
        {timeSince(user.last_seen)}
      </span>
    </div>
  );
}

export default function OnlineUsersBox({ onlineUsers = [] }) {
  const count = onlineUsers.length;

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Activity className="h-4 w-4 text-emerald-600" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            )}
          </div>
          <h3 className="text-sm font-semibold text-zinc-900">Online Now</h3>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          count > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'
        }`}>
          {count} active
        </span>
      </div>

      {count === 0 ? (
        <div className="flex flex-col items-center justify-center py-4 text-zinc-400">
          <Activity className="h-6 w-6 mb-1.5 opacity-40" />
          <p className="text-xs">No users active in last 5 minutes</p>
        </div>
      ) : (
        <div className="max-h-48 overflow-y-auto">
          {onlineUsers.map((user, i) => (
            <UserRow key={user.email || i} user={user} />
          ))}
        </div>
      )}
    </Card>
  );
}
